import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, type MediaItem } from '../../../services/api';
import { useLocale } from '../../../locales';

interface SoundLibraryModalProps {
  onClose: () => void;
  onSelect: (mediaItem: MediaItem) => void;
  currentUrl?: string;
}

// Tree node types
interface FolderNode {
  name: string;
  type: 'folder';
  path: string;
  children: (FolderNode | FileNode)[];
}

interface FileNode {
  name: string;
  type: 'file';
  path: string;
  mediaItem: MediaItem;
}

type TreeNode = FolderNode | FileNode;

// Helper function to check if a node is a folder
const isFolder = (node: TreeNode): node is FolderNode => node.type === 'folder';

// Build tree structure from API response
const buildTreeFromData = (data: any, fallbackName: string): FolderNode[] => {
  const roots: FolderNode[] = [];

  // Process rootImages (root level audio files)
  if (data.rootImages && Array.isArray(data.rootImages)) {
    const audioFiles = data.rootImages.filter((item: MediaItem) => item.type === 'audio');
    if (audioFiles.length > 0) {
      roots.push({
        name: 'rootImages',
        type: 'folder',
        path: 'rootImages',
        children: audioFiles.map((item: MediaItem) => ({
          name: item.name || item.file_name || fallbackName,
          type: 'file',
          path: `rootImages/${item.id || item.url}`,
          mediaItem: item,
        })),
      });
    }
  }

  // Process nested categories (e.g., sesler/images)
  Object.keys(data).forEach((key) => {
    if (key === 'rootImages') return;

    const categoryData = data[key];

    if (typeof categoryData === 'object' && categoryData !== null && !Array.isArray(categoryData)) {
      // This is a nested category like "sesler": { "images": [...] }
      const folder: FolderNode = {
        name: key,
        type: 'folder',
        path: key,
        children: [],
      };

      Object.keys(categoryData).forEach((subKey) => {
        const subData = categoryData[subKey];

        // Check for "images" key which contains the audio files
        if (subKey === 'images' && Array.isArray(subData)) {
          const audioFiles = subData.filter((item: MediaItem) => item.type === 'audio');
          const subFolder: FolderNode = {
            name: subKey,
            type: 'folder',
            path: `${key}/${subKey}`,
            children: audioFiles.map((item: MediaItem) => ({
              name: item.name || item.file_name || fallbackName,
              type: 'file',
              path: `${key}/${subKey}/${item.id || item.url}`,
              mediaItem: item,
            })),
          };
          folder.children.push(subFolder);
        }
      });

      if (folder.children.length > 0) {
        roots.push(folder);
      }
    }
  });

  return roots;
};

const SoundLibraryModal: React.FC<SoundLibraryModalProps> = ({
  onClose,
  onSelect,
  currentUrl,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [treeData, setTreeData] = useState<FolderNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  // Load saved folder path from localStorage on mount
  useEffect(() => {
    const savedPath = localStorage.getItem('sound-library:last-path');
    if (savedPath) {
      try {
        const parsedPath = JSON.parse(savedPath);
        setCurrentPath(parsedPath);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Save current folder path to localStorage whenever it changes
  useEffect(() => {
    if (currentPath.length > 0) {
      localStorage.setItem('sound-library:last-path', JSON.stringify(currentPath));
    } else {
      localStorage.removeItem('sound-library:last-path');
    }
  }, [currentPath]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stop audio if playing
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setPlayingUrl(null);

        // If we're in a subfolder, go back; otherwise close modal
        if (currentPath.length > 0) {
          navigateToFolder(currentPath.slice(0, -1));
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose, currentPath]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response: any = await (apiService as any).request('/media-library/all-audio');

        if (response?.data) {
          const tree = buildTreeFromData(response.data, t("soundLibrary.fallbackName"));
          setTreeData(tree);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t("soundLibrary.loadError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const handleSelect = useCallback(
    (item: MediaItem) => {
      // Stop audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingUrl(null);
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  const handlePlayPreview = useCallback(
    (url: string) => {
      if (audioRef.current) {
        if (playingUrl === url) {
          // Pause if clicking the same audio
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setPlayingUrl(null);
        } else {
          // Play new audio
          audioRef.current.src = url;
          audioRef.current.play().catch((err) => {
            console.error('Audio play error:', err);
          });
          setPlayingUrl(url);

          // Reset when audio ends
          audioRef.current.onended = () => {
            setPlayingUrl(null);
          };
        }
      }
    },
    [playingUrl]
  );

  // Navigate to a folder by path
  const navigateToFolder = useCallback((path: string[]) => {
    setCurrentPath(path);
  }, []);

  // Get the current folder contents
  const getCurrentContents = useCallback((): { folders: FolderNode[]; files: FileNode[] } => {
    let currentNodes: TreeNode[] = treeData;

    // Navigate through the path
    for (const pathSegment of currentPath) {
      const folder = currentNodes.find(
        (node) => isFolder(node) && node.name === pathSegment
      ) as FolderNode | undefined;
      if (folder) {
        currentNodes = folder.children;
      }
    }

    // Separate folders and files
    const folders = currentNodes.filter(isFolder);
    const files = currentNodes.filter((node) => !isFolder(node)) as FileNode[];

    return { folders, files };
  }, [treeData, currentPath]);

  const { folders, files } = getCurrentContents();

  const handleFolderClick = useCallback((folder: FolderNode) => {
    // Stop audio if playing when navigating to folder
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingUrl(null);
    navigateToFolder([...currentPath, folder.name]);
  }, [currentPath, navigateToFolder]);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div className="qe-modal__content qe-modal__content--sound-library" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">{t("soundLibrary.title")}</h2>
            <p className="qe-modal__subtitle">{t("soundLibrary.subtitle")}</p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("soundLibrary.close")}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body">
          {isLoading ? (
            <div className="qe-sound-library__loading">
              <p>{t("soundLibrary.loading")}</p>
            </div>
          ) : error ? (
            <div className="qe-sound-library__error">
              <p>{error}</p>
            </div>
          ) : treeData.length === 0 ? (
            <div className="qe-sound-library__empty">
              <p>{t("soundLibrary.empty")}</p>
            </div>
          ) : (
            <>
              {/* Breadcrumb Navigation */}
              <div className="qe-file-browser__breadcrumb">
                <button
                  className="qe-file-browser__breadcrumb-item"
                  onClick={() => navigateToFolder([])}
                >
                  🏠 {t("soundLibrary.rootFolder")}
                </button>
                {currentPath.map((segment, index) => (
                  <React.Fragment key={segment}>
                    <span className="qe-file-browser__breadcrumb-separator">/</span>
                    <button
                      className="qe-file-browser__breadcrumb-item"
                      onClick={() => navigateToFolder(currentPath.slice(0, index + 1))}
                    >
                      {segment}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Current Folder Contents */}
              <div className="qe-file-browser__content">
                {folders.length === 0 && files.length === 0 ? (
                  <div className="qe-file-browser__empty">
                    <p>{t("soundLibrary.folderEmpty")}</p>
                  </div>
                ) : (
                  <>
                    {/* Folders */}
                    {folders.map((folder) => (
                      <div
                        key={folder.path}
                        className="qe-file-browser__item qe-file-browser__item--folder"
                        onClick={() => handleFolderClick(folder)}
                        onDoubleClick={() => handleFolderClick(folder)}
                      >
                        <span className="qe-file-browser__icon">📁</span>
                        <span className="qe-file-browser__name">{folder.name}</span>
                        <span className="qe-file-browser__count">({folder.children.length})</span>
                        <span className="qe-file-browser__arrow">→</span>
                      </div>
                    ))}

                    {/* Files */}
                    {files.map((file) => {
                      const isSelected = currentUrl === file.mediaItem.url;
                      const isPlaying = playingUrl === file.mediaItem.url;
                      return (
                        <div
                          key={file.path}
                          className={`qe-file-browser__item qe-file-browser__item--file ${
                            isSelected ? 'qe-file-browser__item--selected' : ''
                          } ${isPlaying ? 'qe-file-browser__item--playing' : ''}`}
                        >
                          {/* Play/Pause Button */}
                          <button
                            type="button"
                            className="qe-file-browser__play-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayPreview(file.mediaItem.url);
                            }}
                            aria-label={isPlaying ? t("soundLibrary.stop") : t("soundLibrary.play")}
                          >
                            {isPlaying ? (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <rect x="3" y="2" width="4" height="12" rx="1" />
                                <rect x="9" y="2" width="4" height="12" rx="1" />
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M4 2L12 8L4 14V2Z" />
                              </svg>
                            )}
                          </button>

                          <span className="qe-file-browser__icon">🎵</span>
                          <span
                            className="qe-file-browser__name"
                            title={file.name}
                            onClick={() => handleSelect(file.mediaItem)}
                          >
                            {file.name}
                          </span>
                          {file.mediaItem.size_formatted && (
                            <span className="qe-file-browser__size">{file.mediaItem.size_formatted}</span>
                          )}
                          {isSelected && (
                            <span className="qe-file-browser__check">✓</span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="qe-modal__footer">
          {currentPath.length > 0 && (
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={() => navigateToFolder(currentPath.slice(0, -1))}
            >
              {t("soundLibrary.back")}
            </button>
          )}
          <button
            type="button"
            className="qe-button qe-button--primary"
            onClick={onClose}
          >
            {t("soundLibrary.cancel")}
          </button>
        </div>

        {/* Hidden Audio Element for Preview */}
        <audio ref={audioRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default SoundLibraryModal;

